/*
  Окно «Начисление» (20.10.2026): внести новое или поправить строку.

  Месяц и услуга - ключ строки, поэтому при правке они не меняются: чтобы перенести
  начисление на другой месяц или услугу, его убирают и вносят заново. Это правило
  сервера (`charge_entry_service`), окно только не предлагает недоступного.

  Метка «внесено вручную» ставится сервером: у строки, которую человек тронул,
  ночной обмен с 1С не гасит «пропавший из среза» месяц.
*/

import { useState } from 'react'

import { api } from '../../api/client.js'
import { chargeMonth, lastMonths } from '../../api/format.js'
import { Field } from '../Common.jsx'
import Dropdown from '../Select.jsx'
import FormDialog from './FormDialog.jsx'
import { asDecimal, asInput, isMoney, sameAmount } from './money.js'

const MONTHS = lastMonths(84)

const DUE_HINT =
  'Оставьте пустым - срок возьмётся из настроек организации, как у загруженного файла. ' +
  'Срок не может быть раньше месяца начисления.'

function startForm(charge) {
  if (!charge) return { month: '', service: '', amount: '', paid: '', due: '' }
  return {
    month: String(charge.period).slice(0, 7),
    service: charge.service,
    amount: asInput(charge.amount),
    paid: asInput(charge.paid_amount),
    due: charge.due_date ?? '',
  }
}

/* Деньги сравниваются как суммы, а не как текст: «7100», «7100,00» и «7100.00» - одно и то
   же (ревью 30.10.2026: «7100» против «7100.00» включало «Сохранить»). */
const sameMoney = sameAmount

export default function ChargeDialog({ accountId, charge, onSaved, onClose }) {
  const [start] = useState(() => startForm(charge))
  const [form, setForm] = useState(start)
  const set = (name) => (event) => setForm({ ...form, [name]: event.target.value })
  const editing = Boolean(charge)

  /* Аудит 28.10.2026, раздел 5.3: «Сохранить» без изменений делало строку из 1С «ручной», и
     ночной обмен её переставал обновлять. Без правки кнопка выключена. */
  const changed =
    !sameMoney(form.amount, start.amount) ||
    !sameMoney(form.paid, start.paid) ||
    form.due !== start.due

  const valid =
    isMoney(form.amount) &&
    (form.paid === '' || isMoney(form.paid)) &&
    (editing || (form.month !== '' && form.service.trim() !== '')) &&
    (!editing || changed)

  function submit() {
    const paid = form.paid === '' ? '0' : asDecimal(form.paid)
    const due = form.due === '' ? null : form.due
    if (editing) {
      return api.editCharge(accountId, charge.charge_id, {
        amount: asDecimal(form.amount),
        paid_amount: paid,
        due_date: due,
      })
    }
    return api.addCharge(accountId, {
      period: `${form.month}-01`,
      service: form.service.trim(),
      amount: asDecimal(form.amount),
      paid_amount: paid,
      due_date: due,
    })
  }

  return (
    <FormDialog
      title={editing ? 'Поправить начисление' : 'Внести начисление'}
      note={editing ? `${chargeMonth(charge.period)} · ${charge.service}` : undefined}
      submitLabel={editing ? 'Сохранить' : 'Внести начисление'}
      canSubmit={valid}
      onSubmit={submit}
      onSaved={onSaved}
      onClose={onClose}
    >
      {editing ? null : (
        <div className="row">
          <div className="field field--s">
            <span className="field__label" id="charge-month-label">
              Месяц
            </span>
            <Dropdown
              value={form.month}
              onChange={(month) => setForm({ ...form, month })}
              options={MONTHS}
              empty="выберите"
              labelledBy="charge-month-label"
            />
          </div>
          <Field label="Услуга" width="m">
            <input
              className="field__input"
              value={form.service}
              maxLength={64}
              onChange={set('service')}
              placeholder="например, отопление"
            />
          </Field>
        </div>
      )}
      <div className="row">
        <Field label="Начислено, ₽" width="s">
          <input
            className="field__input"
            inputMode="decimal"
            value={form.amount}
            onChange={set('amount')}
            placeholder="0,00"
          />
        </Field>
        <Field label="Оплачено, ₽" width="s">
          <input
            className="field__input"
            inputMode="decimal"
            value={form.paid}
            onChange={set('paid')}
            placeholder="0,00"
          />
        </Field>
        {/* 23.10.2026, ревью: пустой срок сервер закрывает настройкой организации, как у
            файла; без срока месяц не входил в расчёт пеней и давности. */}
        <Field label="Срок оплаты" width="s" hint={DUE_HINT}>
          <input className="field__input" type="date" value={form.due} onChange={set('due')} />
        </Field>
      </div>
    </FormDialog>
  )
}
